import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const WEB3FORMS_KEY = '34b8690d-595c-4a27-8bfc-5180af547bfd';

export default function Investors() {
  const [form, setForm] = useState({ name: '', title: '', firm: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'investor_page_view');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: 'Alercell — Series A Investor Inquiry',
          ...form,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'investor2_page_view');
        }
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 60, alignItems: 'start' }} className="bio-grid">
            <div>
              <span className="eyebrow">Investor Relations · Series A</span>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.2, marginBottom: 24 }}>
                Early leukemia detection.<br />
                Validated science.<br />
                A category no competitor<br />
                has entered.
              </h1>
              <a href="#materials-form" className="btn btn-primary" style={{ fontSize: 15, padding: '14px 36px', marginBottom: 0 }}>
                Request Investor Materials
              </a>
            </div>
            <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', marginLeft: 40, marginRight: -40, marginTop: 80 }}>
              <img
                src="/investors-closing-gap.jpg"
                alt="Closing the Gap: The Clinical and Economic Imperative for Epigenetic Preemption"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          </div>
          <div style={{ marginTop: 48, paddingTop: 40, borderTop: '1px solid var(--border)' }}>
            <p className="body-lg" style={{ maxWidth: 900 }}>
              Alercell, Inc. is building LENA Platform™, the first agentic diagnostic system for pre-symptomatic leukemia detection. LENA™ operates autonomously within clinical workflows, eliminating manual integration friction while reading epigenetic patterns years before conventional tests turn positive. The company's agentic architecture, combining AI-powered analysis with autonomous clinical integration, creates a new diagnostic category with no incumbent competitor and a business model that scales profitably with volume.
            </p>
          </div>
        </div>
      </section>

      {/* Science Stats */}
      <div className="metric-strip">
        <div className="container">
          <div className="metric-grid">
            {[
              { value: '41 mo', label: 'Pre-symptomatic detection lead time validated' },
              { value: 'AUC 0.967', label: 'Platform diagnostic accuracy (ALER-5 cohort)' },
              { value: '199,800', label: 'Patient records — development dataset (EMR)' },
              { value: '0', label: 'Commercial incumbents in pre-leukemic methylation diagnostics' },
            ].map((m, i) => (
              <div key={i} className="metric-item">
                <span className="metric-value">{m.value}</span>
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The Opportunity */}
      <section id="opportunity" className="section section-surface">
        <div className="container">
          <span className="eyebrow">A Large Problem with a Missing Solution</span>
          <h2 className="section-headline" style={{ maxWidth: 800, marginBottom: 24 }}>
            Cancer diagnostics has detected disease the same way for 150 years. We are changing when detection happens.
          </h2>
          <div className="cyan-line" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60 }} className="bio-grid">
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>The Clinical & Economic Gap</h3>
              <p className="body-md" style={{ marginBottom: 16 }}>
                In 2025, more than 2 million Americans will receive a cancer diagnosis, and 618,120 will die from the disease. For hematologic malignancies, the five-year survival rate for acute myeloid leukemia diagnosed at advanced stages remains below 30% — while early-stage AML carries a survival rate above 60%. The difference is not the biology; it is when detection occurs.
              </p>
              <p className="body-md" style={{ marginBottom: 12 }}>
                The direct medical cost of cancer in the United States reached $183 billion in 2015, with projections estimating $296 billion by 2030 — costs driven overwhelmingly by late-stage treatment. Earlier detection is both a clinical and an economic imperative.
              </p>
              <p className="source-cite">Sources: ACS Cancer Facts & Figures 2025; NCI Cancer Trends Progress Report 2024</p>
            </div>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>The Missing Solution</h3>
              <p className="body-md" style={{ marginBottom: 16 }}>
                No currently available diagnostic assay detects leukemia before symptoms appear. The nearest alternatives — conventional cytogenetics, NGS-based liquid biopsy — read the disease after transformation is underway. They are confirmation tools, not prevention tools.
              </p>
              <p className="body-md" style={{ marginBottom: 16 }}>
                Alercell's LENA platform is designed to operate upstream of genetic mutation — in the epigenetic layer where leukemogenesis begins, years before any conventional test turns positive.
              </p>
              <div style={{ background: 'var(--accent-cyan-dim)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius-sm)', padding: '16px 20px' }}>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--accent-cyan)' }}>
                  This is a category that does not yet have a commercial incumbent. Alercell intends to define it.
                </p>
              </div>
            </div>
          </div>
          <style>{`@media(max-width:768px){.bio-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      {/* Why Alercell */}
      <section id="why" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">Four Reasons This Platform Is Defensible</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>
            Validated science. Proprietary data. Protected IP. No incumbent.
          </h2>
          <div className="cyan-line" />
          <div className="differentiator-grid">
            {[
              { n: '01', title: 'Data at Scale', body: 'The ALER-5 cohort — 538,090 clinical observations from 199,800 patient EMRs across 5 independent cohorts — is one of the largest EMR-linked datasets in hematologic oncology. A dataset at this scale is a capital and time barrier that is extremely difficult to replicate.' },
              { n: '02', title: 'Proprietary Data Asset', body: 'The ALER-5 dataset is not publicly available. It represents a trained, calibrated platform that took years and significant scientific capital to build. Data moats compound: the platform improves with every new clinical interaction.' },
              { n: '03', title: 'Patent-Protected IP', body: '24+ patent applications covering both biology and AI architecture; the LENA Code® normalization engine, LENA Clock™ & LENA Score™ algorithms, m5C-ESSENTIAL™ tRNA methylation assay, and the epigenetic orbital conservation framework. IP portfolio under active prosecution.' },
              { n: '04', title: 'Creating a New Category', body: "No commercial incumbent exists for predictive pre-leukemic detection via epigenetic biomarkers. Alercell's proprietary LENA Platform differs fundamentally from commodity methylation assays. This is category creation, not market competition." },
            ].map((d, i) => (
              <div key={i} className="diff-card">
                <div className="diff-number">{d.n}</div>
                <div className="diff-title">{d.title}</div>
                <p className="diff-desc">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Expansion Teaser */}
      <section style={{ background: 'var(--bg-primary)', padding: '60px 0', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ background: 'linear-gradient(135deg, rgba(0,174,239,0.08) 0%, rgba(0,200,150,0.06) 100%)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius)', padding: '48px 56px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 40, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: 12 }}>Platform Expansion</span>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, lineHeight: 1.3 }}>
                The LENA Platform is architected for multi-cancer expansion. Leukemia is the beachhead.
              </h3>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                From leukemia validation to lymphoma scale-up to solid tumor expansion by 2028 — one platform, five cancers, 888,000+ annual patient opportunities. No technology reinvention required.
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              <Link to="/expansion" className="btn btn-primary" style={{ fontSize: 14, padding: '14px 28px' }}>
                View Platform Expansion Strategy →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Non-Dilutive Funding */}
      <section id="grants" className="section section-surface">
        <div className="container">
          <span className="eyebrow">Non-Dilutive Funding Strategy</span>
          <h2 className="section-headline" style={{ maxWidth: 780, marginBottom: 20 }}>
            Alercell is pursuing non-dilutive funding from federal science agencies to validate platform merit and accelerate clinical research.
          </h2>
          <div className="cyan-line" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40 }} className="bio-grid">
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Active Grant Applications</h3>
              <p className="body-md" style={{ marginBottom: 16 }}>
                Alercell has submitted applications to the National Institutes of Health (NIH/NCI) and other federal science agencies for funding of prospective clinical validation studies. Applications are under peer review; advancement through federal grant cycles demonstrates scientific merit validation by independent government reviewers.
              </p>
              <p style={{ fontSize: 13, color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                Current status: Applications in evaluation phase. Federal funding timelines and agency priorities (including temporary program suspensions) mean decisions are expected through 2026.
              </p>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Strategic Value</h3>
              <p className="body-md" style={{ marginBottom: 16 }}>
                Federal grant funding serves as independent third-party validation of scientific and clinical merit. Peer-review panels at NIH and NSF apply rigorous standards; award of such funding would underscore the platform's scientific credibility and reduce dependence on equity capital for clinical validation costs.
              </p>
              <p className="body-md">
                Alercell remains committed to securing non-dilutive funding to advance the prospective validation study while maintaining optimal capital efficiency.
              </p>
            </div>
          </div>
          <style>{`@media(max-width:768px){.bio-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      {/* Contact Form */}
      <section id="materials-form" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: 680 }}>
          <span className="eyebrow">Request Materials</span>
          <h2 className="section-headline" style={{ marginBottom: 12 }}>Interested in learning more? We'd like to hear from you.</h2>
          <p className="body-md" style={{ marginBottom: 40 }}>
            Complete the form below and Frederic Scheer will be in touch directly. Investor materials are shared through a private, secure channel following an introductory conversation.
          </p>
          {status === 'success' ? (
            <div style={{ background: 'var(--accent-cyan-dim)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius)', padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>Thank you.</p>
              <p className="body-md">Frederic Scheer will be in touch within 2 business days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-input" type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your full name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Title / Role</label>
                  <input className="form-input" type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Managing Partner, GP, etc." />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Firm / Organization</label>
                <input className="form-input" type="text" value={form.firm} onChange={e => setForm({...form, firm: e.target.value})} placeholder="Fund or organization name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input className="form-input" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+1 (555) 000-0000" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Message (Optional)</label>
                <textarea className="form-textarea" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us a bit about your investment focus or any specific questions." />
              </div>
              {status === 'error' && (
                <p style={{ fontSize: 13, color: '#ef4444', marginTop: 16, fontFamily: 'var(--font-mono)' }}>
                  Something went wrong. Please try again or email info@alercell.com directly.
                </p>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={status === 'submitting'}
                style={{ width: '100%', justifyContent: 'center', padding: '15px', fontSize: 15, opacity: status === 'submitting' ? 0.6 : 1, cursor: status === 'submitting' ? 'wait' : 'pointer' }}
              >
                {status === 'submitting' ? 'Sending…' : 'Get in Touch'}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
